import { Link } from "react-router-dom";
import useSEO from "../hooks/useSEO";

const LAST_UPDATE = "15 février 2026";

export default function Privacy() {
  useSEO({
    title: "Politique de confidentialité",
    description: "Politique de protection des renseignements personnels de DemkaBiz, conforme à la Loi 25 du Québec.",
  });
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="privacy-page">
      <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">Confidentialité</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-foreground tracking-tight mb-2">Politique de protection des renseignements personnels</h1>
      <p className="text-sm text-muted-foreground mb-10">Conforme à la Loi 25 du Québec · Dernière mise à jour : {LAST_UPDATE}</p>

      <div className="card-surface p-5 mb-8 border-primary/30 bg-primary/5">
        <p className="text-sm text-foreground"><strong>En résumé :</strong> nous collectons uniquement les renseignements nécessaires au fonctionnement de DemkaBiz, nous ne les vendons jamais, vous pouvez les consulter, les corriger ou les supprimer à tout moment en écrivant à <a href="mailto:privacy@demka.ca" className="text-primary underline">privacy@demka.ca</a>.</p>
      </div>

      <div className="prose prose-stone max-w-none space-y-2">
        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">1. Responsable du traitement</h2>
        <p><strong>Demka Services Inc.</strong>, 20, boul. Charest Ouest, Bureau 102, Québec (Québec) G1K 1X2, Canada — est responsable du traitement des renseignements personnels collectés sur DemkaBiz.</p>

        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">2. Responsable de la protection des renseignements personnels</h2>
        <p>Conformément à l'article 3.1 de la Loi 25, nous avons désigné un responsable de la protection des renseignements personnels (RPRP) :</p>
        <ul>
          <li>Nom : <em>[À COMPLÉTER]</em></li>
          <li>Courriel : <a href="mailto:privacy@demka.ca" className="text-primary">privacy@demka.ca</a></li>
          <li>Adresse postale : 20, boul. Charest Ouest, Bureau 102, Québec (Québec) G1K 1X2</li>
        </ul>

        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">3. Renseignements que nous collectons</h2>
        <h3 className="font-display font-semibold text-xl text-foreground mt-4">3.1 Lors de votre inscription</h3>
        <ul>
          <li>Nom complet</li>
          <li>Adresse courriel</li>
          <li>Numéro de téléphone (facultatif)</li>
          <li>Mot de passe (stocké uniquement sous forme chiffrée — bcrypt)</li>
          <li>Rôle choisi (client, professionnel, entrepreneur, organisme)</li>
        </ul>
        <h3 className="font-display font-semibold text-xl text-foreground mt-4">3.2 Si vous créez un profil prestataire</h3>
        <ul>
          <li>Nom de l'organisation, photo/logo, description, catégories de services</li>
          <li>Localisation (province, ville), langues parlées</li>
          <li>Coordonnées professionnelles (téléphone, courriel, WhatsApp, site web)</li>
        </ul>
        <h3 className="font-display font-semibold text-xl text-foreground mt-4">3.3 Lors de l'usage de la Plateforme</h3>
        <ul>
          <li>Adresse IP, type de navigateur, pages consultées (à des fins statistiques)</li>
          <li>Demandes de service que vous soumettez (description, urgence, budget)</li>
          <li>Avis et notes que vous publiez</li>
          <li>Cookies fonctionnels (voir <Link to="/cookies" className="text-primary">Politique relative aux cookies</Link>)</li>
        </ul>

        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">4. Finalités du traitement</h2>
        <p>Vos renseignements sont utilisés exclusivement pour :</p>
        <ul>
          <li>Permettre votre inscription et l'authentification sécurisée;</li>
          <li>Afficher publiquement votre fiche (uniquement si vous êtes prestataire et après votre validation explicite);</li>
          <li>Mettre en relation clients et prestataires;</li>
          <li>Vous envoyer les communications transactionnelles nécessaires (vérification d'email, réinitialisation de mot de passe, notification d'une demande);</li>
          <li>Améliorer la Plateforme par des statistiques agrégées et anonymes;</li>
          <li>Répondre à nos obligations légales (lutte contre la fraude, demandes judiciaires).</li>
        </ul>

        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">5. Base légale</h2>
        <p>Le traitement repose sur votre <strong>consentement</strong> (article 12 Loi 25), donné lors de l'inscription et matérialisé par l'acceptation des présentes. Pour les cookies non essentiels, votre consentement est recueilli via la bannière dédiée.</p>

        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">6. Destinataires des renseignements</h2>
        <p>Vos renseignements ne sont jamais vendus. Ils peuvent être communiqués à :</p>
        <ul>
          <li>Notre équipe interne (administrateurs et modérateurs);</li>
          <li>Nos sous-traitants techniques sous contrat de confidentialité : <strong>Emergent</strong> (hébergement & stockage), <strong>Resend</strong> (envoi d'emails transactionnels), <strong>MongoDB Atlas</strong> (base de données);</li>
          <li>Les autres utilisateurs de la Plateforme, uniquement pour les informations que vous choisissez de rendre publiques (votre fiche prestataire);</li>
          <li>Les autorités compétentes en cas d'obligation légale.</li>
        </ul>

        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">7. Transferts hors Québec</h2>
        <p>Certains de nos sous-traitants peuvent traiter des renseignements hors du Québec (notamment aux États-Unis et en Europe). Conformément à l'article 17 de la Loi 25, nous avons réalisé une évaluation des facteurs relatifs à la vie privée et nous assurons par contrat que la protection accordée est équivalente à celle prévue par la loi québécoise.</p>

        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">8. Durées de conservation</h2>
        <ul>
          <li>Compte utilisateur : tant que le compte est actif, puis 12 mois après désactivation;</li>
          <li>Demandes de service : 24 mois;</li>
          <li>Données de facturation (lorsqu'applicables) : 7 ans (obligation comptable);</li>
          <li>Journaux de connexion : 12 mois;</li>
          <li>Cookies : 13 mois maximum.</li>
        </ul>

        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">9. Vos droits</h2>
        <p>Conformément à la Loi 25, vous disposez des droits suivants :</p>
        <ul>
          <li><strong>Accès</strong> : consulter les renseignements que nous détenons à votre sujet;</li>
          <li><strong>Rectification</strong> : corriger un renseignement inexact;</li>
          <li><strong>Désindexation</strong> : demander la suppression d'un lien rendu accessible par un moteur de recherche;</li>
          <li><strong>Suppression</strong> : demander la suppression de vos renseignements;</li>
          <li><strong>Portabilité</strong> : recevoir vos données dans un format technologique structuré couramment utilisé;</li>
          <li><strong>Retrait du consentement</strong> à tout moment;</li>
          <li><strong>Plainte</strong> auprès de la <a href="https://www.cai.gouv.qc.ca" target="_blank" rel="noreferrer" className="text-primary">Commission d'accès à l'information du Québec</a>.</li>
        </ul>
        <p>Pour exercer ces droits, écrivez à <a href="mailto:privacy@demka.ca" className="text-primary">privacy@demka.ca</a>. Nous vous répondons dans un délai maximal de 30 jours.</p>

        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">10. Sécurité</h2>
        <p>Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos renseignements : chiffrement TLS en transit, mots de passe hachés avec bcrypt, jetons d'authentification JWT, limitation des tentatives de connexion, journalisation des accès, sauvegardes régulières et chiffrées.</p>
        <p>En cas d'incident de confidentialité présentant un risque sérieux de préjudice, nous vous en aviserons sans délai conformément à l'article 3.5 de la Loi 25.</p>

        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">11. Mineurs</h2>
        <p>DemkaBiz n'est pas destinée aux personnes mineures de moins de 14 ans. Nous ne collectons pas sciemment de renseignements concernant un mineur de moins de 14 ans sans le consentement parental.</p>

        <h2 className="font-display font-semibold text-2xl text-foreground mt-8">12. Modification de la politique</h2>
        <p>Nous nous réservons le droit de modifier cette politique. Toute modification substantielle vous sera notifiée par courriel ou par avis sur la Plateforme.</p>
      </div>
    </div>
  );
}
